import type { AlertDialog } from '@base-ui/react';

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { Client } from '~/api/clients/types';

import { useClients } from '~/api/clients';
import { createAppColumnHelper } from '~/components';
import { ColumnIds } from '~/constants/table';
import { useIntl } from '~/hooks/useIntl';
import { formatVatNumber } from '~/utils/string';

import { ClientContactsCell } from './ClientsTableCells/ClientContactsCell';
import { ClientMobileCell } from './ClientsTableCells/ClientMobileCell';
import { ClientNameCell } from './ClientsTableCells/ClientNameCell';
import { ClientOptionsCell } from './ClientsTableCells/ClientOptionsCell';
import { ClientTypeCell } from './ClientsTableCells/ClientTypeCell';
import { ClientVatCell } from './ClientsTableCells/ClientVatCell';

export type DialogHandles = Record<'deleteClientHandle', AlertDialog.Handle<Client>>;

const columnHelper = createAppColumnHelper<Client>();

export const useClientsTableColumns = ({ dialogHandles }: { dialogHandles: DialogHandles }) => {
  const filterOptions = useClientsTableFilterOptions();

  const { t } = useTranslation(['clients']);

  return useMemo(
    () =>
      columnHelper.columns([
        // Name
        columnHelper.accessor('name', {
          id: ColumnIds.clientsName,
          header: t('clients:overview.columns.name'),
          cell: ({ row: { original: client } }) => <ClientNameCell client={client} />,
          meta: {
            styles: { size: '5fr', minSize: 150 },
            isResizable: true,
            filterOptions: filterOptions.names,
          },
        }),

        // Client type
        columnHelper.accessor('clientType', {
          id: ColumnIds.clientsType,
          header: t('clients:overview.columns.type'),
          cell: ({ row: { original: client } }) => <ClientTypeCell type={client.clientType} />,
          enableGlobalFilter: false,
          meta: {
            styles: { size: '3fr', minSize: 100 },
            isResizable: true,
            filterOptions: filterOptions.clientTypes,
          },
        }),

        // Vat number or other number
        columnHelper.accessor(({ otherNumber, vatNumber }) => (vatNumber ? formatVatNumber(vatNumber) : otherNumber || undefined), {
          id: ColumnIds.clientsVat,
          header: t('clients:overview.columns.vatOrOtherNumber'),
          cell: ({ getValue, row: { original: client } }) => <ClientVatCell client={client} value={getValue()} />,
          sortUndefined: 'last',
          meta: {
            styles: { size: '4fr', minSize: 150 },
            isResizable: true,
          },
        }),

        // Contacts
        columnHelper.accessor(
          ({ clientType, contacts }) =>
            clientType === 'organisation'
              ? contacts
                  .filter(({ deleted }) => !deleted)
                  .map(({ fullName }) => fullName)
                  .join(' ')
              : undefined,
          {
            id: ColumnIds.clientsContacts,
            header: t('clients:overview.columns.contacts'),
            cell: ({ row: { original: client } }) => <ClientContactsCell client={client} />,
            sortUndefined: 'last',
            meta: {
              styles: { size: '4fr', minSize: 150 },
              isResizable: true,
            },
          },
        ),

        // Options
        columnHelper.display({
          id: ColumnIds.options,
          cell: ({ row: { original: client } }) => <ClientOptionsCell client={client} dialogHandles={dialogHandles} />,
          meta: {
            styles: { size: 'max-content', minSize: 40 },
          },
        }),

        // Mobile
        columnHelper.display({
          id: ColumnIds.clientsMobile,
          cell: ({ row: { original: client } }) => <ClientMobileCell client={client} dialogHandles={dialogHandles} />,
          meta: { mobile: true },
        }),
      ]),
    [filterOptions, t, dialogHandles],
  );
};

const useClientsTableFilterOptions = () => {
  const { data: clients } = useClients();

  const { t } = useTranslation(['clients']);
  const { compareForSort } = useIntl();

  const nameOptions = useMemo(
    () => [...new Set(clients.map((client) => client.name))].sort(compareForSort).map((name) => ({ label: name, value: name })),
    [clients, compareForSort],
  );

  const clientTypeOptions = useMemo(
    () =>
      (['organisation', 'private'] as const).map((clientType) => {
        return { label: t(`clients:type.${clientType}`), value: clientType };
      }),
    [t],
  );

  return useMemo(
    () => ({
      names: nameOptions,
      clientTypes: clientTypeOptions,
    }),
    [nameOptions, clientTypeOptions],
  );
};
